import { ChangeNames, FormModel, unwrap } from "../../clients/FirebaseClient/types";
import produce, { Draft } from "immer";
import { useFormikContext } from "formik";
import { get, set, isEqual } from "lodash";
import { useDispatch } from "react-redux";
import { useTypedSelector, ConfigurationActions, ScreenActions, RootDispatch } from "../../redux";
import { useState, useEffect, useCallback } from "react";
import { defaultFirebaseClient } from "../../clients/FirebaseClient";
import { selectChange } from "../../clients/ConfigurationClient/tools";

type ModelModifier<T extends ChangeNames> = (x: Draft<FormModel<T>>) => void;
// type FormModelHelpers<T extends ChangeNames> = {
//     values: FormModel<T>;
//     setValues: (x: ModelModifier<T>) => FormModel<T>;
//     getFieldValue: <U extends string>(path: U | undefined) => GetFieldType<ChangeTypes, U>;
//     setFieldValue: <U>(path: string | undefined, value: U) => void;
//     getFieldProps: <U>(path: string | undefined) => FieldInputProps<U>;
//     submitChange: (newValues: FormModel<T>) => void;
// };
export const useFormModel = <T extends ChangeNames>() /*: FormModelHelpers<T> */ => {
    const dispatch: RootDispatch = useDispatch();
    const [mounted, setMounted] = useState(false);
    const { configurationId, uid } = useTypedSelector((state) => ({
        configurationId: state.configuration.currentConfigurationId || "",
        uid: state.auth.uid || "",
    }));
    const { values, setValues: _setValues, getFieldProps } = useFormikContext<FormModel<T>>();

    useEffect(() => {
        setMounted(true);
        return () => {
            setMounted(false);
        };
    }, [setMounted]);

    return {
        values,
        setValues: useCallback(
            (x: ModelModifier<T>): typeof values => {
                const newValues = produce(values, x);
                mounted && _setValues(newValues);
                return newValues;
            },
            [mounted, _setValues, values],
        ),
        getFieldValue: (path: string | undefined) => {
            return path ? get(values, path) : undefined;
        },
        setFieldValue: <U extends {}>(path: string | undefined, value: U) => {
            if (path && path && mounted) {
                _setValues(
                    produce(values, (v) => {
                        set(v, path, value);
                    }),
                );
            }
        },
        getFieldProps: (path: string | undefined) => {
            const fieldProps = getFieldProps(path);
            // setting a value prevents the input to become an uncontrolled component.
            if (typeof fieldProps.value === "undefined") {
                fieldProps.value = "";
            }
            return fieldProps;
        },
        submitChange: async (newValues: typeof values) => {
            dispatch(ScreenActions.setActivity());
            try {
                if (!configurationId) return;
                // gather changeset
                const changeSet = (Object.keys(newValues) as Array<ChangeNames>)
                    .filter((changeName) => selectChange(changeName)?.relevant)
                    .filter((changeName) => {
                        return !isEqual(
                            newValues[changeName as keyof typeof values],
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            selectChange(changeName)?.state!,
                        );
                    })
                    .map((changeName) => ({
                        [changeName]: unwrap(newValues[changeName as keyof typeof values]),
                    }))
                    .reduce((a, b) => ({ ...a, ...b }), {});
                console.log("changeset", changeSet);
                await defaultFirebaseClient.doWithOperation(
                    uid,
                    async () =>
                        await dispatch(
                            ConfigurationActions.change({
                                confId: configurationId,
                                conf: changeSet,
                            }),
                        ),
                );
            } finally {
                dispatch(ScreenActions.finishActivity());
            }
        },
    };
};
